import { useCallback, useSyncExternalStore } from "react";

// Keep in sync with $bp-xl in atlas/styles/breakpoints.scss.
export const MEDIA_QUERY_BELOW_XL = "(max-width: 1279.98px)";

// Keep in sync with $bp-lg in atlas/styles/breakpoints.scss.
export const MEDIA_QUERY_BELOW_LG = "(max-width: 1023.98px)";

const canMatchMedia = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function";

const getSnapshot = (query: string): boolean =>
  canMatchMedia() ? window.matchMedia(query).matches : false;

const subscribe = (query: string, callback: () => void): (() => void) => {
  if (!canMatchMedia()) return () => {};

  const mql = window.matchMedia(query);
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
};

/**
 * Tracks whether a media query currently matches, re-rendering on change.
 * SSR-safe: getServerSnapshot returns false so prerender never assumes mobile.
 */
export function useMediaQuery(query: string): boolean {
  const subscribeToQuery = useCallback(
    (callback: () => void) => subscribe(query, callback),
    [query]
  );
  const getQuerySnapshot = useCallback(() => getSnapshot(query), [query]);

  return useSyncExternalStore(subscribeToQuery, getQuerySnapshot, () => false);
}
